import { Box, Divider, Grid, InputAdornment, Tab, Tabs, useMediaQuery } from '@material-ui/core';
import { useTheme, makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import BusinessIcon from '@material-ui/icons/Business';
import MessageIcon from '@material-ui/icons/Message';
import { useFormikContext } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Permissions } from '../../../../shared/auth/constants/permissions';
import { useHasPermission } from '../../../Session';
import { SettingValueUnit } from '../../../api';
import Accordion from '../../../components/Accordion';
import { useFlutter } from '../../../components/FlutterProvider';
import { useLoadingController } from '../../../components/LoadingProvider';
import TabPanel from '../../../components/TabPanel';
import BooleanCheckbox from '../../../components/fields/BooleanCheckbox';
import OutlinedDoubleInputField from '../../../components/fields/OutlinedDoubleInputField';
import OutlinedInputField from '../../../components/fields/OutlinedInputField';
import OutlinedSelectField from '../../../components/fields/OutlinedSelectField';
import Switch from '../../../components/fields/Switch';
import useCommonStyles from '../../../useCommonStyles';
import { useMessageTemplateOptionsQuery } from '../../../utilities/useMessageTemplateOptions';
import { FormValues } from '../shared';
import SettingsLogoField from './SettingsLogoField';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        tabsRoot: {
            width: '100%',
            [theme.breakpoints.down('sm')]: {
                marginTop: theme.spacing(10),
                padding: theme.spacing(3),
                paddingRight: theme.spacing(3),
            },
        },
        tabsFlexContainer: {
            justifyContent: 'flex-end',
        },
        tabLabel: {
            display: 'inline-flex',
            alignItems: 'center',
        },
        tabLabelIcon: {
            marginRight: theme.spacing(1),
        },
        sectionSpacing: {
            marginTop: theme.spacing(2),
        },
    })
);

const SettingsBody = () => {
    const { t } = useTranslation(['settingsPage']);
    const { capabilities, deviceFingerPrint } = useFlutter();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const commonStyles = useCommonStyles();
    const hasPermissionToEditMessageTemplates = useHasPermission(Permissions.SETTINGS_UPDATE_TEMPLATE_USE_CASES);
    const hasPermissionToUpdateCompanySettings = useHasPermission(Permissions.SETTINGS_UPDATE_COMPANY_SETTINGS);
    const { options: templatesOptions, loading: messageTemplatesLoading } = useMessageTemplateOptionsQuery(
        !hasPermissionToEditMessageTemplates
    );
    const { values } = useFormikContext<FormValues>();

    useLoadingController(messageTemplatesLoading);

    // As dicussion with PM, the PTI has no mobile support yet
    // we can hide biometric and push notification settings if the device doesn't support it.
    // This will avoid confusion for users
    const isDeviceCompatibleWithBiometric = capabilities.hasFingerPrint || capabilities.hasFaceID;
    const outlinedSelectInputProps = {
        classes: {
            icon: commonStyles.fillSecondary,
        },
    };
    const nonNegativeIntegerInputProps = {
        min: 0,
        inputMode: 'numeric' as const,
        pattern: '[0-9]*',
    };
    const nonNegativeNumberInputProps = {
        min: 0,
        inputMode: 'decimal' as const,
        pattern: '[0-9]*[.]?[0-9]*',
    };
    const minsAdornment = <InputAdornment position="end">{t('settingsPage:label.mins')}</InputAdornment>;
    const amountAdornment = <InputAdornment position="end">{values.companyCurrency}</InputAdornment>;
    const pctAdornment = <InputAdornment position="end">%</InputAdornment>;
    const settingValueUnitOptions = [
        {
            value: SettingValueUnit.Amount,
            label: values.companyCurrency,
        },
        {
            value: SettingValueUnit.Percentage,
            label: '%',
        },
    ];
    const biddingTemplateFields = [
        {
            label: t('settingsPage:label.messageTemplateUseCases.upcomingBid'),
            name: 'upcomingBidTemplateUseCase',
        },
        {
            label: t('settingsPage:label.messageTemplateUseCases.bidOpen'),
            name: 'biddingOpenedTemplateUseCase',
        },
        {
            label: t('settingsPage:label.messageTemplateUseCases.bidSubmission'),
            name: 'bidSubmissionTemplateUseCase',
        },
        {
            label: t('settingsPage:label.messageTemplateUseCases.highestBidSubmission'),
            name: 'highestBidSubmissionTemplateUseCase',
        },
        {
            label: t('settingsPage:label.messageTemplateUseCases.noChancesLeft'),
            name: 'noChancesLeftTemplateUseCase',
        },
        {
            label: t('settingsPage:label.messageTemplateUseCases.bidAwarded'),
            name: 'bidAwardedTemplateUseCase',
        },
        {
            label: t('settingsPage:label.messageTemplateUseCases.bidAwardedForSalesConsultant'),
            name: 'bidAwardedForSalesConsultantTemplateUseCase',
        },
    ];
    const userAccountTemplateFields = [
        {
            label: t('settingsPage:label.messageTemplateUseCases.otp'),
            name: 'otpTemplateUseCase',
        },
        {
            label: t('settingsPage:label.messageTemplateUseCases.userAccountRejection'),
            name: 'userAccountRejectionTemplateUseCase',
        },
        {
            label: t('settingsPage:label.messageTemplateUseCases.userAccountApproval'),
            name: 'userAccountApprovalTemplateUseCase',
        },
    ];

    const renderMessageTemplateFields = (fields: Array<{ label: string; name: string }>) =>
        fields.map(field => (
            <Grid key={field.name} xs={12} item>
                <OutlinedSelectField
                    color="secondary"
                    disabled={!hasPermissionToEditMessageTemplates}
                    inputProps={outlinedSelectInputProps}
                    label={field.label}
                    name={field.name}
                    options={templatesOptions}
                />
            </Grid>
        ));

    const defaultTab = useMemo(() => {
        if (hasPermissionToUpdateCompanySettings) {
            return 'companySettings';
        }
        if (hasPermissionToEditMessageTemplates) {
            return 'templatesSettings';
        }

        return '';
    }, [hasPermissionToUpdateCompanySettings, hasPermissionToEditMessageTemplates]);

    const [value, setValue] = useState(defaultTab);

    const styles = useStyles();

    return (
        <>
            <Tabs
                classes={{ root: commonStyles.subMenusRoot, indicator: commonStyles.subMenusindicator }}
                indicatorColor="secondary"
                onChange={(event, newValue) => setValue(newValue)}
                scrollButtons="auto"
                textColor="secondary"
                value={value}
            >
                {hasPermissionToUpdateCompanySettings && (
                    <Tab
                        classes={{
                            root: commonStyles.subMenuTabRoot,
                            selected: commonStyles.subMenuTabSelected,
                            textColorSecondary: commonStyles.subMenusTextColor,
                        }}
                        label={
                            <Box className={styles.tabLabel} component="span">
                                <BusinessIcon className={styles.tabLabelIcon} fontSize="small" />
                                <span>{t('settingsPage:tabs.companySettings')}</span>
                            </Box>
                        }
                        value="companySettings"
                    />
                )}
                {hasPermissionToEditMessageTemplates && (
                    <Tab
                        classes={{
                            root: commonStyles.subMenuTabRoot,
                            selected: commonStyles.subMenuTabSelected,
                            textColorSecondary: commonStyles.subMenusTextColor,
                        }}
                        label={
                            <Box className={styles.tabLabel} component="span">
                                <MessageIcon className={styles.tabLabelIcon} fontSize="small" />
                                <span>{t('settingsPage:tabs.templatesSettings')}</span>
                            </Box>
                        }
                        value="templatesSettings"
                    />
                )}
            </Tabs>
            <Box ml={isSmall ? 2 : 0} mr={isSmall ? 2 : 0} mt={isSmall ? 12 : 0}>
                <TabPanel index="templatesSettings" value={value}>
                    <Grid spacing={2} container>
                        <Grid hidden={!isDeviceCompatibleWithBiometric} lg={6} md={6} sm={12} xs={12} item>
                            <Accordion title={t('settingsPage:accordionTitle.login')} defaultExpanded>
                                <Grid spacing={2} container>
                                    <Grid xs={12} item>
                                        <Switch
                                            label={t('settingsPage:label.biometric')}
                                            labelPlacement="start"
                                            name="biometric"
                                        />
                                    </Grid>
                                </Grid>
                            </Accordion>
                        </Grid>
                        <Grid hidden={!isDeviceCompatibleWithBiometric} lg={6} md={6} sm={12} xs={12} item>
                            <Accordion title={t('settingsPage:accordionTitle.notifications')} defaultExpanded>
                                <Grid spacing={2} container>
                                    <Grid xs={12} item>
                                        <Switch
                                            disabled={!deviceFingerPrint}
                                            label={t('settingsPage:label.pushNotification')}
                                            labelPlacement="start"
                                            name="pushNotification"
                                        />
                                    </Grid>
                                </Grid>
                            </Accordion>
                        </Grid>
                        {hasPermissionToEditMessageTemplates && (
                            <>
                                <Grid lg={6} md={6} sm={12} xs={12} item>
                                    <Accordion
                                        title={t('settingsPage:accordionTitle.selectMessageTemplatesForBidding')}
                                        defaultExpanded
                                    >
                                        <Grid spacing={2} container>
                                            {renderMessageTemplateFields(biddingTemplateFields)}
                                        </Grid>
                                    </Accordion>
                                </Grid>
                                <Grid lg={6} md={6} sm={12} xs={12} item>
                                    <Accordion
                                        title={t('settingsPage:accordionTitle.selectMessageTemplatesForUserAccount')}
                                        defaultExpanded
                                    >
                                        <Grid spacing={2} container>
                                            {renderMessageTemplateFields(userAccountTemplateFields)}
                                        </Grid>
                                    </Accordion>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </TabPanel>
                {hasPermissionToUpdateCompanySettings && (
                    <TabPanel index="companySettings" value={value}>
                        <Grid spacing={2} container>
                            <Grid lg={6} md={6} sm={12} xs={12} item>
                                <Accordion title={t('settingsPage:accordionTitle.company')} defaultExpanded>
                                    <Grid spacing={2} container>
                                        <Grid xs={12} item>
                                            <OutlinedInputField
                                                color="secondary"
                                                label={t('settingsPage:label.companyName')}
                                                name="companyName"
                                                required
                                            />
                                        </Grid>
                                        <Grid xs={12} item>
                                            <OutlinedInputField
                                                color="secondary"
                                                label={t('settingsPage:label.contactEmail')}
                                                name="companyContactEmail"
                                                required
                                            />
                                        </Grid>
                                        <Grid sm={6} xs={12} item>
                                            <OutlinedInputField
                                                color="secondary"
                                                label={t('settingsPage:label.currencySymbol')}
                                                name="companyCurrency"
                                                required
                                            />
                                        </Grid>
                                        <Grid sm={6} xs={12} item>
                                            <OutlinedInputField
                                                color="secondary"
                                                label={t('settingsPage:label.currencyCode')}
                                                name="companyCurrencyCode"
                                                required
                                            />
                                        </Grid>
                                        <Grid sm={6} xs={12} item>
                                            <OutlinedInputField
                                                color="secondary"
                                                label={t('settingsPage:label.timezone')}
                                                name="companyTimezone"
                                                required
                                            />
                                        </Grid>
                                        <Grid sm={6} xs={12} item>
                                            <OutlinedInputField
                                                color="secondary"
                                                label={t('settingsPage:label.countryCode')}
                                                name="companyCountryCode"
                                                required
                                            />
                                        </Grid>
                                        <Grid sm={6} xs={12} item>
                                            <SettingsLogoField
                                                emptyLabelKey="settingsPage:label.noLogo"
                                                labelKey="settingsPage:label.logo"
                                                name="companyLogo"
                                            />
                                        </Grid>
                                        <Grid sm={6} xs={12} item>
                                            <SettingsLogoField
                                                emptyLabelKey="settingsPage:label.noFavicon"
                                                labelKey="settingsPage:label.favicon"
                                                name="companyFavicon"
                                            />
                                        </Grid>
                                    </Grid>
                                </Accordion>
                            </Grid>
                            <Grid lg={6} md={6} sm={12} xs={12} item>
                                <Accordion title={t('settingsPage:accordionTitle.bidding')} defaultExpanded>
                                    <Grid spacing={2} container>
                                        <Grid xs={12} item>
                                            <OutlinedInputField
                                                color="secondary"
                                                inputProps={{ ...nonNegativeIntegerInputProps, min: 1 }}
                                                label={t('settingsPage:label.maxDealCount')}
                                                name="maxDealCount"
                                                type="number"
                                                required
                                            />
                                        </Grid>
                                        <Grid xs={12} item>
                                            <OutlinedInputField
                                                color="secondary"
                                                inputProps={{ ...nonNegativeIntegerInputProps, min: 1 }}
                                                label={t('settingsPage:label.topDealCount')}
                                                name="topDealCount"
                                                type="number"
                                                required
                                            />
                                        </Grid>
                                        <Grid xs={12} item>
                                            <OutlinedInputField
                                                color="secondary"
                                                inputProps={{ ...nonNegativeIntegerInputProps, min: 1 }}
                                                label={t('settingsPage:label.maxSalesTransactionPhotos')}
                                                name="maxSalesTransactionPhotos"
                                                type="number"
                                                required
                                            />
                                        </Grid>
                                        <Grid xs={12} item>
                                            <Divider className={commonStyles.divider} />
                                        </Grid>
                                        <Grid xs={8} item>
                                            <OutlinedDoubleInputField
                                                label={t('settingsPage:label.handlingChargesDefaults')}
                                                required
                                            >
                                                <OutlinedInputField
                                                    color="secondary"
                                                    endAdornment={amountAdornment}
                                                    inputProps={nonNegativeIntegerInputProps}
                                                    name="handlingFeesAmount"
                                                    type="number"
                                                />
                                                <OutlinedInputField
                                                    color="secondary"
                                                    endAdornment={pctAdornment}
                                                    inputProps={nonNegativeNumberInputProps}
                                                    name="handlingFeesPercentage"
                                                    type="number"
                                                />
                                            </OutlinedDoubleInputField>
                                        </Grid>
                                        <Grid xs={4} item>
                                            <OutlinedSelectField
                                                color="secondary"
                                                inputProps={outlinedSelectInputProps}
                                                label={t('settingsPage:label.unit')}
                                                name="handlingFeesDefaultUnit"
                                                options={settingValueUnitOptions}
                                                required
                                            />
                                        </Grid>

                                        <Grid xs={12} item>
                                            <Divider className={commonStyles.divider} />
                                        </Grid>

                                        <Grid xs={8} item>
                                            <OutlinedDoubleInputField
                                                label={t('settingsPage:label.marginDefaults')}
                                                required
                                            >
                                                <OutlinedInputField
                                                    color="secondary"
                                                    endAdornment={amountAdornment}
                                                    inputProps={nonNegativeIntegerInputProps}
                                                    name="marginAmount"
                                                    type="number"
                                                    required
                                                />
                                                <OutlinedInputField
                                                    color="secondary"
                                                    endAdornment={pctAdornment}
                                                    inputProps={nonNegativeNumberInputProps}
                                                    name="marginPercentage"
                                                    type="number"
                                                />
                                            </OutlinedDoubleInputField>
                                        </Grid>
                                        <Grid sm={4} item>
                                            <OutlinedSelectField
                                                color="secondary"
                                                inputProps={outlinedSelectInputProps}
                                                label={t('settingsPage:label.unit')}
                                                name="marginDefaultUnit"
                                                options={settingValueUnitOptions}
                                            />
                                        </Grid>
                                        <Grid xs={12} item>
                                            <Divider className={commonStyles.divider} />
                                        </Grid>
                                        <Grid sm={6} xs={12} item>
                                            <OutlinedInputField
                                                color="secondary"
                                                endAdornment={minsAdornment}
                                                inputProps={nonNegativeIntegerInputProps}
                                                label={t('settingsPage:label.urgentBidPeriod')}
                                                name="urgentBidPeriod"
                                                type="number"
                                                required
                                            />
                                        </Grid>
                                        <Grid sm={6} xs={12} item>
                                            <OutlinedInputField
                                                color="secondary"
                                                endAdornment={minsAdornment}
                                                inputProps={nonNegativeIntegerInputProps}
                                                label={t('settingsPage:label.notUrgentBidPeriod')}
                                                name="notUrgentBidPeriod"
                                                type="number"
                                                required
                                            />
                                        </Grid>
                                        <Grid xs={12} item>
                                            <BooleanCheckbox
                                                color="secondary"
                                                label={t('settingsPage:label.enableUpcomingBidNotification')}
                                                name="enableUpcomingBidNotification"
                                            />
                                        </Grid>
                                    </Grid>
                                </Accordion>
                            </Grid>
                        </Grid>
                    </TabPanel>
                )}
            </Box>
        </>
    );
};

export default SettingsBody;
